import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ImageWithFallback } from "@/components/common/ImageWithFallback";
import { toast } from "sonner";
import { resolveReferralCode } from "@/utils/referralUtils";
import ForgotPasswordDialog from "@/components/auth/ForgotPasswordDialog";

// Flag to control Google authentication visibility - can be moved to environment variables later
const ENABLE_GOOGLE_AUTH = false;

const formSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
});

const MobileLoginForm: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  // Check for referral codes on page load - store them for later use
  React.useEffect(() => {
    const refCode = resolveReferralCode();
    if (refCode) {
      // Store for later attribution after login
      localStorage.setItem('pendingReferralCode', refCode);
    }
  }, []);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    setLoginError(null);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });
      
      if (error) {
        if (error.message.includes("Invalid login credentials") || 
            error.message.includes("Invalid email or password")) {
          setLoginError("Incorrect email or password. Please try again.");
          toast.error("Incorrect email or password");
        } else {
          setLoginError(error.message);
          toast.error(error.message);
        }
        return;
      }
      
      // Check if there's a pending referral code to process
      const pendingReferralCode = localStorage.getItem('pendingReferralCode');
      if (pendingReferralCode && data.user) {
        localStorage.removeItem('pendingReferralCode');
      }
      
      toast.success("Logged in successfully!");
      navigate("/");
    } catch (error) {
      setLoginError("An unexpected error occurred. Please try again.");
      toast.error("An unexpected error occurred");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  // Google login function kept but won't be used until re-enabled
  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setLoginError(null);
    
    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
        }
      });
      
      if (error) {
        setLoginError(error.message);
        toast.error(error.message);
      }
    } catch (error) {
      setLoginError("Failed to connect with Google. Please try again.");
      toast.error("Failed to connect with Google");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center">
      <div className="w-full max-w-md px-4 py-8">
        <div className="relative z-10 overflow-hidden rounded-xl bg-black/80 backdrop-blur-xl shadow-xl border border-white/10 px-6 py-8">
          {/* Logo */}
          <div className="mb-6 flex justify-center">
            <div className="w-44 h-16 relative">
              <ImageWithFallback 
                src="https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo%20trade%20bot%20365.png" 
                alt="Trade Bot 365 Logo" 
                className="w-full h-full object-contain"
                loading="eager"
                skeletonClassName="bg-muted/50"
              />
            </div>
          </div>
          
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-xl font-bold text-white mb-1">Welcome Back</h1>
            <p className="text-sm text-white/70">Sign in to your account to continue</p>
          </div>
          
          {/* Error message */}
          {loginError && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-md">
              <p className="text-sm text-red-200">{loginError}</p>
            </div>
          )}
          
          {/* Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 h-4 w-4 text-white/50" />
                      <FormControl>
                        <Input 
                          placeholder="Email Address" 
                          className="pl-9 bg-white/10 border-white/10 text-white placeholder:text-white/50 focus:border-botscheme-ocean-blue h-10"
                          {...field} 
                        />
                      </FormControl>
                    </div>
                    <FormMessage className="text-xs text-red-300" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="relative">
                      <Lock className="absolute left-3 top-2.5 h-4 w-4 text-white/50" />
                      <FormControl>
                        <Input 
                          type={showPassword ? "text" : "password"} 
                          placeholder="Password" 
                          className="pl-9 bg-white/10 border-white/10 text-white placeholder:text-white/50 focus:border-botscheme-ocean-blue h-10"
                          {...field} 
                        />
                      </FormControl>
                      <button 
                        type="button" 
                        className="absolute right-3 top-2.5 text-white/50"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <FormMessage className="text-xs text-red-300" />
                  </FormItem>
                )}
              />
              
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-botscheme-ocean-blue to-botscheme-royal-purple hover:opacity-90 transition-all h-11 mt-2" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : "Sign In"}
              </Button>

              <div className="text-center">
                <Button 
                  type="button" 
                  variant="link" 
                  className="text-botscheme-ocean-blue hover:text-botscheme-royal-purple transition-colors text-sm p-0"
                  onClick={() => setForgotPasswordOpen(true)}
                >
                  Forgot your password?
                </Button>
              </div>
            </form>
          </Form>
          
          {/* Google OAuth Section - Hidden based on flag */}
          {ENABLE_GOOGLE_AUTH && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-black/80 px-2 text-white/50">Or continue with</span>
                </div>
              </div>
              
              <Button 
                variant="outline" 
                className="w-full border-white/10 bg-white/5 text-white hover:bg-white/10 transition-all h-11" 
                onClick={handleGoogleLogin}
                disabled={isLoading}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Sign in with Google
              </Button>
            </>
          )}
          
          <div className="text-center mt-6">
            <Button 
              variant="link" 
              className="text-botscheme-ocean-blue hover:text-botscheme-royal-purple transition-colors text-sm p-0"
              onClick={() => navigate("/auth/register")}
            >
              Don't have an account? Sign up
            </Button>
          </div>
          
          {/* Decorative background elements */}
          <div className="absolute -z-10 top-10 right-10 w-20 h-20 rounded-full bg-botscheme-royal-purple/20 blur-xl"></div>
          <div className="absolute -z-10 bottom-10 left-10 w-24 h-24 rounded-full bg-botscheme-ocean-blue/20 blur-xl"></div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog 
        open={forgotPasswordOpen} 
        onOpenChange={setForgotPasswordOpen} 
      />
      
      {/* Full screen background gradient */}
      <div className="fixed inset-0 -z-20 bg-gradient-to-br from-black via-botscheme-royal-purple/10 to-botscheme-ocean-blue/10"></div>
    </div>
  );
};

export default MobileLoginForm;
